import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import { eventHolidayMonthQueryRange, eventsHolidaysCacheKey } from "./eventsSlice"

function flattenHolidaysForClient(byMonth, clientId) {
  const prefix = `${String(clientId)}|`
  const seen = new Set()
  const out = []
  Object.entries(byMonth || {}).forEach(([key, rows]) => {
    if (!key.startsWith(prefix) || !Array.isArray(rows)) return
    rows.forEach((row) => {
      const id = row?.id ?? row?.eventId
      if (id != null) {
        const sid = String(id)
        if (seen.has(sid)) return
        seen.add(sid)
      }
      out.push(row)
    })
  })
  return out
}

// Async thunk for fetching holidays
export const fetchHolidays = createAsyncThunk(
  "holidays/fetch",
  async ({ clientId, date, year, month, startDate, endDate, force = false }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      if (!clientId) {
        throw new Error("Client ID is required")
      }

      const cacheKey = eventsHolidaysCacheKey({ clientId, date, year, month, startDate, endDate })
      if (!force && state.holidays.fetchedMonths?.[cacheKey]) {
        return {
          data: state.holidays.holidaysByMonth?.[cacheKey] || [],
          fromCache: true,
          cacheKey,
          clientId,
        }
      }

      const { start_date, end_date } = eventHolidayMonthQueryRange({ date, year, month, startDate, endDate })
      const queryParams = new URLSearchParams({
        start_date,
        end_date,
      }).toString()
      const data = await get(`events/clients/${clientId}/holidays?${queryParams}`)
      return { data, fromCache: false, cacheKey, clientId }
    } catch (err) {
      console.error("Holidays fetch error:", err)
      return rejectWithValue(err?.message || "Failed to fetch holidays")
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      if (!arg?.clientId) return false
      const key = eventsHolidaysCacheKey(arg)
      const slice = getState().holidays ?? {}
      if (slice.fetchedMonths?.[key]) return false
      if (slice.inFlightMonths?.[key]) return false
      return true
    },
  },
)

const initialState = {
  holidays: [],
  status: "idle", // idle | loading | succeeded | failed
  error: null,
  holidaysByMonth: {},
  fetchedMonths: {},
  inFlightMonths: {},
}

const holidaysSlice = createSlice({
  name: "holidays",
  initialState,
  reducers: {
    clearHolidays: (state) => {
      state.holidays = []
      state.status = "idle"
      state.error = null
      state.holidaysByMonth = {}
      state.fetchedMonths = {}
      state.inFlightMonths = {}
    },
    hydrateHolidaysForClient: (state, action) => {
      state.holidays = flattenHolidaysForClient(state.holidaysByMonth, action.payload)
    },
    clearHolidaysError: (state) => {
      state.error = null
    },
    resetHolidaysOnLogout: (state) => {
      state.holidays = []
      state.status = "idle"
      state.error = null
      state.holidaysByMonth = {}
      state.fetchedMonths = {}
      state.inFlightMonths = {}
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHolidays.pending, (state, action) => {
        const key = eventsHolidaysCacheKey(action.meta?.arg || {})
        if (!state.inFlightMonths) state.inFlightMonths = {}
        if (key) state.inFlightMonths[key] = true
        if (action.meta?.arg?.force) {
          state.status = "loading"
          state.error = null
          return
        }
        if (state.fetchedMonths?.[key]) return
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchHolidays.fulfilled, (state, action) => {
        state.status = "succeeded"
        const payload = action.payload
        const fromCache = payload?.fromCache
        const cacheKey = payload?.cacheKey
        const clientId = payload?.clientId
        const data = fromCache || cacheKey ? payload?.data : payload
        if (cacheKey && state.inFlightMonths) delete state.inFlightMonths[cacheKey]
        if (!fromCache && cacheKey && Array.isArray(data)) {
          if (!state.holidaysByMonth) state.holidaysByMonth = {}
          if (!state.fetchedMonths) state.fetchedMonths = {}
          state.holidaysByMonth[cacheKey] = data
          state.fetchedMonths[cacheKey] = true
        }
        state.holidays = flattenHolidaysForClient(state.holidaysByMonth, clientId)
      })
      .addCase(fetchHolidays.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || "Failed to fetch holidays"
        const key = eventsHolidaysCacheKey(action.meta?.arg || {})
        if (key && state.inFlightMonths) delete state.inFlightMonths[key]
      })
  },
})

export const { clearHolidays, clearHolidaysError, resetHolidaysOnLogout, hydrateHolidaysForClient } =
  holidaysSlice.actions

export default holidaysSlice.reducer
